import { Button, Col, Drawer, Form, Input, Row, Space, Spin, Radio, Upload, message, Modal } from 'antd'
import { useForm } from 'antd/es/form/Form'
import { ValidateErrorEntity } from 'rc-field-form/lib/interface'
import { FC, useState, useCallback, useRef, useEffect } from 'react'
import { UploadOutlined } from '@ant-design/icons'
import type { CustomContentAdminDTO, CustomContentFormValues } from './models'
import { ViewType, ViewTypeLabels } from './models'
import { useCreateCustomContentMutation, useUpdateCustomContentMutation } from './queries'
import API from '../../services/DSApiService'
import { ImageCropperInline } from './ImageCropper'
import { CropperState, CropperImage, CropperTransitions } from 'react-advanced-cropper'


interface CustomContentEditorFormProps {
  content?: CustomContentAdminDTO
  visible: boolean
  onCreate?: () => void
  onCancel?: (e) => void
  onClose?: (e) => void
}

const requiredMessage = 'Kötelező megadni'
const requiredRule = { required: true, message: requiredMessage }

const CustomContentEditorDrawerForm: FC<CustomContentEditorFormProps> = ({
  visible,
  content,
  onCancel,
  onCreate,
  onClose,
}) => {
  const [form] = useForm<CustomContentFormValues>()
  const [viewType, setViewType] = useState<ViewType>(
    content?.viewType as unknown as ViewType || ViewType.FULLSCREEN_IMAGE
  )
  const [uploadedFileName, setUploadedFileName] = useState<string | undefined>(
    content?.imageFileName
  )
  const [localImageUrl, setLocalImageUrl] = useState<string | undefined>()
  const [tempImageUrl, setTempImageUrl] = useState<string>()
  const [croppedImageBlob, setCroppedImageBlob] = useState<Blob>()

  const createMutation = useCreateCustomContentMutation(
    (data) => {
      message.success('Egyedi tartalom sikeresen létrehozva')
      onCreate?.()
    },
    (error) => {
      message.error('Hiba történt a tartalom létrehozásakor')
      console.error(error)
    }
  )

  const updateMutation = useUpdateCustomContentMutation(
    (data) => {
      message.success('Egyedi tartalom sikeresen frissítve')
      onCreate?.()
    },
    (error) => {
      message.error('Hiba történt a tartalom frissítésénél')
      console.error(error)
    }
  )

  const initialValues: Partial<CustomContentFormValues> = content
    ? {
        name: content.name,
        viewType: content.viewType as unknown as ViewType,
      }
    : {
        viewType: ViewType.FULLSCREEN_IMAGE,
      }

  // Handle file selection - set up image for cropping
  const handleFileSelect = useCallback((file: File) => {
    const url = URL.createObjectURL(file)
    setTempImageUrl(url)
    return false // Prevent auto upload
  }, [])

  // Handle crop complete - just store the cropped image, don't upload yet
  const handleCropComplete = useCallback(async (croppedImage: Blob) => {
    setCroppedImageBlob(croppedImage)

    // Create preview URL
    const previewUrl = URL.createObjectURL(croppedImage)
    setLocalImageUrl(previewUrl)
    form.setFieldsValue({ imageFile: croppedImage as any })
  }, [])

  const handleSubmit = useCallback(async () => {
    form
      .validateFields()
      .then(async (values) => {
        let finalFileName = uploadedFileName

        // If there's a cropped image, upload it now
        if (croppedImageBlob) {
          try {
            // Delete old image if exists
            if (uploadedFileName) {
              try {
                await API.deleteCustomContentImage(uploadedFileName)
              } catch (deleteError) {
                console.warn('Failed to delete old image:', deleteError)
                // Continue with upload even if delete fails
              }
            }

            const file = new File([croppedImageBlob], 'cropped-image.jpg', { type: 'image/jpeg' })
            const response = await API.uploadCustomContentImage(file)
            message.success('Kép sikeresen feltöltve')
            finalFileName = response.data.fileName
            form.setFieldsValue({ imageFile: file as any })
          } catch (error) {
            message.error('Kép feltöltése sikertelen')
            console.error(error)
            return
          }
        }

        if (!finalFileName) {
          message.error('Kérjük, töltsön fel egy képet')
          return
        }

        const requestData = {
          name: values.name,
          imageFileName: finalFileName,
          viewType: values.viewType,
        }

        if (content) {
          // Update existing
          updateMutation.mutate({
            id: content.id,
            request: requestData as any,
          })
        } else {
          // Create new
          createMutation.mutate(requestData as any)
        }
        form.resetFields()
      })
      .catch((errorInfo: ValidateErrorEntity<CustomContentFormValues>) => {
        console.error('Validation failed:', errorInfo)
      })
  }, [content, uploadedFileName, croppedImageBlob, form, createMutation, updateMutation])

  useEffect(() => {
    // When content changes, update the ref
    prevViewTypeRef.current = content?.viewType as unknown as ViewType || ViewType.FULLSCREEN_IMAGE
  }, [content])

  useEffect(() => {
    // When editing existing content with an image, load it into the cropper
    if (content?.imageUrl && !tempImageUrl) {
      setTempImageUrl(content.imageUrl)
    }
  }, [content, tempImageUrl])

  useEffect(() => {
    // Reset temp image when drawer is closed
    if (!visible) {
      setTempImageUrl(undefined)
    }
  }, [visible])

  const prevViewTypeRef = useRef<ViewType>(viewType)

  const hasInfoHeader = (type: ViewType) =>
    type === ViewType.GATE_INFO_HEADER || type === ViewType.GENERAL_INFO_HEADER

  const getCropperHeight = (type: ViewType) =>
    type === ViewType.FULLSCREEN_IMAGE ? 480 : 240

  const isSignificantChange = (oldType: ViewType, newType: ViewType) =>
    hasInfoHeader(oldType) !== hasInfoHeader(newType)

  const resetCroppedImage = () => {
    setTempImageUrl(undefined)
    setCroppedImageBlob(undefined)
    setLocalImageUrl(undefined)
    //also reset imageFile form field
    form.setFieldsValue({imageFile: undefined})
  }

  const handleViewTypeChange = (newViewType: ViewType) => {
    const oldViewType = prevViewTypeRef.current

    if ((tempImageUrl || localImageUrl || content?.imageUrl) && isSignificantChange(oldViewType, newViewType)) {
      Modal.confirm({
        title: 'Nézettípus megváltoztatása',
        content:
          'A képarány meg fog változni, ezért a jelenlegi vágás elvész. Újra ki kell választania és meg kell vágnia a képet. Biztosan folytatja?',
        okText: 'Igen, folytatom',
        cancelText: 'Mégsem',
        onOk: () => {
          resetCroppedImage()
          setViewType(newViewType)
          prevViewTypeRef.current = newViewType
          form.setFieldsValue({ viewType: newViewType, imageFile: undefined })
          setUploadedFileName(undefined)
        },
        onCancel: () => {
          // Revert the radio button selection in the form
          form.setFieldsValue({ viewType: oldViewType })
        },
      })
    } else {
      setViewType(newViewType)
      prevViewTypeRef.current = newViewType
      form.setFieldsValue({ viewType: newViewType })
    }
  }

  const formButtons = (
    <Row justify="end">
      <Col>
        <Space>
          <Button
            onClick={(evt): void => {
              form.resetFields()
              setLocalImageUrl(undefined)
              setUploadedFileName(undefined)
              setTempImageUrl(undefined)
              onCancel?.(evt)
            }}
          >
            Mégsem
          </Button>
          <Button
            type="primary"
            loading={createMutation.isLoading || updateMutation.isLoading}
            onClick={handleSubmit}
          >
            Mentés
          </Button>
        </Space>
      </Col>
    </Row>
  )


  return (
    <Spin spinning={createMutation.isLoading || updateMutation.isLoading}>
      <Drawer
        title={content ? 'Egyedi tartalom szerkesztése' : 'Egyedi tartalom létrehozása'}
        placement="right"
        width="700px"
        open={visible}
        extra={formButtons}
        onClose={onClose}
      >
        <Form
          layout="vertical"
          form={form}
          initialValues={initialValues}
          autoComplete="off"
        >
          <Form.Item
            label="Elnevezés"
            name="name"
            rules={[
              requiredRule,
              { max: 128, message: 'Maximum 128 karakter' }
            ]}
          >
            <Input
              placeholder="Adja meg a tartalom nevét"
              maxLength={128}
              showCount
            />
          </Form.Item>

          <Form.Item
            label="Nézettípus"
            name="viewType"
            rules={[requiredRule]}
          >
            <Radio.Group
              onChange={(e) => handleViewTypeChange(e.target.value)}
            >
              <Space direction="vertical">
                {Object.values(ViewType).map((type) => (
                  <Radio key={type} value={type}>
                    {ViewTypeLabels[type]}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label="Kép"
            name="imageFile"
            rules={[
              {
                required: !uploadedFileName,
                message: 'Kérjük, töltsön fel egy képet'
              }
            ]}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Upload
                accept="image/*"
                maxCount={1}
                showUploadList={false}
                beforeUpload={handleFileSelect}
              >
                <Button icon={<UploadOutlined />}>
                  Kép kiválasztása és vágása
                </Button>
              </Upload>

              <Row gutter={16}>
                <Col span={24}>
                  {tempImageUrl ? (
                    <ImageCropperInline
                      imageUrl={tempImageUrl}
                      viewType={viewType}
                      onCropComplete={handleCropComplete}
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: `${getCropperHeight(viewType)}px`,
                      backgroundColor: '#f5f5f5',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#999',
                      fontSize: '16px'
                    }}>
                      Válasszon képet a vágáshoz
                    </div>
                  )}
                </Col>
              </Row>
            </Space>
          </Form.Item>
        </Form>
        {formButtons}
      </Drawer>
    </Spin>
  )
}

export default CustomContentEditorDrawerForm
